import type { QueryClient } from '@tanstack/react-query'

import { createRootRouteWithContext, Outlet } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { Provider } from 'jotai'
import { Loader2 } from 'lucide-react'

import type { TProfile } from '@/api/auth/dto'
import type { TProfileResponse } from '@/api/auth/types'

import { api } from '@/api'
import { ExportPollingManager } from '@/components/global/export-polling-manager'
import SessionExpiryWarning from '@/components/global/session-expiry-warning'
import ErrorComp from '@/components/pages/ErrorComp'
import NotFoundPage from '@/components/pages/NotFoundPage'
import { Toaster } from '@/components/ui/sonner'
import { env } from '@/env'

import TanstackQueryLayout from '../integrations/tanstack-query/layout'

interface MyRouterContext {
  queryClient: QueryClient
  profile: TProfile | undefined | null
  isAuthenticated: boolean
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  head: () => ({
    meta: [{ title: env.VITE_APP_TITLE }]
  }),
  beforeLoad: async ({ context: { queryClient } }) => {
    // Cek apakah ada token di localStorage
    const token = localStorage.getItem('__app-access-token')

    if (!token) {
      return {
        profile: null,
        isAuthenticated: false
      }
    }

    try {
      const profile = await queryClient.fetchQuery(api.auth.getProfile())
      await queryClient.ensureQueryData(api.auth.getProfile())

      return {
        profile,
        isAuthenticated: !!profile
      }
    } catch {
      // Jika terjadi error (termasuk network error), coba ambil dari cache
      const cachedProfile = localStorage.getItem('__app-profile-data')

      if (cachedProfile) {
        try {
          const profile = JSON.parse(cachedProfile)
          return {
            profile: profile as TProfileResponse,
            isAuthenticated: true
          }
        } catch {
          // Jika parsing gagal, hapus cache yang corrupt
          localStorage.removeItem('__app-profile-data')
        }
      }

      return {
        profile: null as TProfileResponse | null,
        isAuthenticated: false
      }
    }
  },
  notFoundComponent: NotFoundPage,
  pendingComponent: PendingComponentroot,
  component: () => (
    <>
      <Provider>
        <Outlet />
        <Toaster position="bottom-right" />
        <TanStackRouterDevtools />
        <TanstackQueryLayout />
        <ExportPollingManager />
        <SessionExpiryWarning />
      </Provider>
    </>
  ),
  errorComponent: err => {
    return <ErrorComp err={err} />
  }
})

function PendingComponentroot() {
  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-6">
        {/* Logo atau branding */}
        <div className="flex flex-col items-center gap-2">
          <img
            src="/assets/images/logo-ful-new.png"
            alt="SIMLUHTAN"
            className="h-12 w-auto"
            onError={e => {
              // Fallback jika logo tidak ada
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              const parent = target.parentElement
              if (parent) {
                parent.innerHTML =
                  '<h1 class="text-2xl font-bold text-primary">SIMLUHTAN</h1>'
              }
            }}
          />
        </div>
        {/* Loading Spinner */}
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="text-primary size-8 animate-spin" />
          <p className="text-sm text-gray-600">Memuat aplikasi...</p>
        </div>
      </div>
    </div>
  )
}
